import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronRight, Video, Image as ImageIcon, ExternalLink, FileText, Scissors } from 'lucide-react';
import api from '../../lib/api';
import { fromSlug, jobUrlSegment } from '../../lib/slug';
import { Submission } from '../../types';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { formatDate } from '../../lib/utils';
import { AIContentQAPanel } from '../../components/AIContentQAPanel';
import { useAuthStore } from '../../stores/authStore';

export default function SubmissionDetailPage() {
  const { id: rawId } = useParams();
  const id = fromSlug(rawId || '');
  const navigate = useNavigate();
  const [submission, setSubmission] = useState<Submission | null>(null);
  const [loading, setLoading] = useState(true);
  const user = useAuthStore((s) => s.user);
  const canRunQA = user?.role === 'AGENCY' || user?.role === 'BRAND' || user?.role === 'ADMIN';

  useEffect(() => {
    loadSubmission();
  }, [id]);

  const loadSubmission = async () => {
    try {
      const response = await api.get(`/submissions/${id}`);
      setSubmission(response.data);
    } catch (error) {
      console.error('Failed to load submission:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'SUBMITTED':
        return <Badge variant="warning">Pending Review</Badge>;
      case 'APPROVED':
        return <Badge variant="success">Approved</Badge>;
      case 'REVISION_REQUESTED':
        return <Badge variant="destructive">Revision Requested</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12 text-glass-text-2">Loading...</div>;
  }

  if (!submission) {
    return <div className="text-center py-12 text-glass-text-3">Submission not found</div>;
  }

  const job = submission.invitation?.job;
  const campaignName = job?.title ?? 'Campaign';
  const brandName = job?.brandOrg?.name ?? '';
  const kolName = submission.invitation?.kolProfile?.user?.fullName ?? submission.invitation?.kolProfile?.name ?? 'KOL';
  const jobId = job?.id;

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1 text-sm text-glass-text-3">
        <button
          onClick={() => navigate('/agency/submissions')}
          className="hover:text-accent2 transition-colors"
        >
          Submissions
        </button>
        {jobId && (
          <>
            <ChevronRight className="w-4 h-4" />
            <button
              onClick={() =>
                navigate(`/agency/campaigns/${jobUrlSegment({ id: jobId!, title: campaignName, urlSlug: job?.urlSlug })}`)
              }
              className="hover:text-accent2 transition-colors truncate max-w-[200px]"
            >
              {campaignName}
            </button>
          </>
        )}
        <ChevronRight className="w-4 h-4" />
        <span className="text-glass-text font-medium">Submission</span>
      </nav>

      {/* Header with Back Button */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() =>
            jobId
              ? navigate(`/agency/campaigns/${jobUrlSegment({ id: jobId, title: campaignName, urlSlug: job?.urlSlug })}`)
              : navigate(-1)
          }
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-[2.25rem] font-semibold tracking-tightest text-glass-text">{campaignName}</h1>
            {getStatusBadge(submission.status)}
          </div>
          <p className="text-glass-text-2 mt-1">{brandName}</p>
        </div>
      </div>

      {/* Submission Metadata */}
      <Card>
        <CardHeader>
          <CardTitle>Submission Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <span className="text-xs text-glass-text-3 uppercase tracking-[0.05em] block mb-0.5">Campaign</span>
              <span className="font-medium text-glass-text">{campaignName}</span>
            </div>
            <div>
              <span className="text-xs text-glass-text-3 uppercase tracking-[0.05em] block mb-0.5">KOL</span>
              <span className="font-medium text-glass-text">{kolName}</span>
              {submission.invitation?.kolProfile?.user?.email && (
                <span className="text-sm text-glass-text-3 block">{submission.invitation.kolProfile.user.email}</span>
              )}
            </div>
            <div>
              <span className="text-xs text-glass-text-3 uppercase tracking-[0.05em] block mb-0.5">Version</span>
              <span className="font-medium text-glass-text">v{submission.version}</span>
            </div>
            <div>
              <span className="text-xs text-glass-text-3 uppercase tracking-[0.05em] block mb-0.5">Status</span>
              {getStatusBadge(submission.status)}
            </div>
            <div>
              <span className="text-xs text-glass-text-3 uppercase tracking-[0.05em] block mb-0.5">Submitted By</span>
              <span className="font-medium text-glass-text">{submission.createdBy?.fullName ?? submission.createdBy?.email ?? '—'}</span>
            </div>
            <div>
              <span className="text-xs text-glass-text-3 uppercase tracking-[0.05em] block mb-0.5">Date</span>
              <span className="font-medium text-glass-text">{formatDate(submission.createdAt)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Caption Text */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Caption Text
          </CardTitle>
        </CardHeader>
        <CardContent>
          {submission.captionText ? (
            <p className="text-glass-text-2 whitespace-pre-wrap">{submission.captionText}</p>
          ) : (
            <p className="text-glass-text-3 italic">No caption provided</p>
          )}
        </CardContent>
      </Card>

      {canRunQA && (
        <AIContentQAPanel
          mode="submission"
          entityId={submission.id}
          description="Pre-screen caption and metadata against the campaign brief before agency or brand review."
        />
      )}

      {/* Media Gallery */}
      {submission.media && submission.media.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ImageIcon className="w-5 h-5" />
              Media
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {submission.media.map((media) => (
                <div key={media.id} className="rounded-xl border border-glass overflow-hidden bg-glass-2">
                  {media.type === 'IMAGE' ? (
                    <a
                      href={media.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block"
                    >
                      <img
                        src={media.url}
                        alt={media.title ?? 'Submitted content'}
                        className="w-full h-48 object-cover object-center hover:opacity-90 transition-opacity"
                      />
                    </a>
                  ) : (
                    <a
                      href={media.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 p-4 text-accent2 hover:underline"
                    >
                      {media.type === 'VIDEO' ? (
                        <Video className="w-5 h-5 text-purple-500 dark:text-purple-300 flex-shrink-0" />
                      ) : (
                        <ExternalLink className="w-5 h-5 text-accent2 flex-shrink-0" />
                      )}
                      <span className="text-sm truncate flex-1">
                        {media.type === 'VIDEO' ? 'Video' : media.type === 'LINK' ? 'Link' : media.type === 'FILE' ? 'File' : 'Media'}
                      </span>
                      <ExternalLink className="w-3 h-3 flex-shrink-0" />
                    </a>
                  )}
                  {media.type === 'VIDEO' && (
                    <div className="px-3 pb-3">
                      <button
                        type="button"
                        onClick={() =>
                          navigate(
                            `/agency/ai-video-edits/new?submissionId=${encodeURIComponent(submission.id)}&mediaId=${encodeURIComponent(media.id)}`
                          )
                        }
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-accent2 hover:underline"
                      >
                        <Scissors className="w-3.5 h-3.5" />
                        AI video edits
                      </button>
                    </div>
                  )}
                  {media.title && (
                    <p className="text-xs text-glass-text-3 p-3 pt-0">{media.title}</p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Revision Comments */}
      {submission.revisionComments && (
        <Card className="border-amber-400/30 bg-amber-400/5">
          <CardHeader>
            <CardTitle className="text-amber-500 dark:text-amber-300">Revision Comments</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-glass-text whitespace-pre-wrap">{submission.revisionComments}</p>
            <p className="text-sm text-glass-text-3 mt-2">
              Approval or revision decisions are handled by the brand team.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
